import { useCallback, useMemo, useState } from 'react';

export type SortDirection = 'asc' | 'desc';

type SortValue = string | number | null | undefined;

// Nulls always sort last, regardless of direction, so an undetermined/missing value never jumps
// to the top of a descending sort.
function compareSortValues(a: SortValue, b: SortValue, direction: SortDirection): number {
  if (a == null && b == null) {
    return 0;
  }
  if (a == null) {
    return 1;
  }
  if (b == null) {
    return -1;
  }

  const base = typeof a === 'number' && typeof b === 'number' ? a - b : String(a).localeCompare(String(b));
  return direction === 'asc' ? base : -base;
}

export function useTableSort<T, F extends string>(
  rows: Array<T>,
  getSortValue: (row: T, field: F) => SortValue,
  defaultField: F,
  defaultDirection: SortDirection = 'asc',
) {
  const [sortField, setSortField] = useState<F>(defaultField);
  const [sortDirection, setSortDirection] = useState<SortDirection>(defaultDirection);

  const handleSortChange = useCallback(
    (field: F) => {
      setSortDirection((current) => (sortField === field && current === 'asc' ? 'desc' : 'asc'));
      setSortField(field);
    },
    [sortField],
  );

  const sortedRows = useMemo(
    () =>
      [...rows].sort((a, b) =>
        compareSortValues(getSortValue(a, sortField), getSortValue(b, sortField), sortDirection),
      ),
    [rows, sortField, sortDirection, getSortValue],
  );

  return { sortField, sortDirection, handleSortChange, sortedRows };
}
